/** Props for {@link Wordmark}. */
export interface WordmarkProps {
  /** Height/width of the orbital logo mark in pixels. Text scales alongside. */
  logoSize?: number;
  /** Optional extra classes. */
  className?: string;
  /**
   * Optional extra classes for the "DEPLOYHUB" text only — e.g. the app
   * shell's top bar hides it below `sm`, keeping just the mark, because at
   * 320-375 px the full lockup plus the top-bar controls overflowed the
   * page horizontally (found by the Task 25 e2e, ruling R13).
   */
  textClassName?: string;
}

/**
 * DeployHub lockup: the icon-only orbital mark next to the two-tone wordmark
 * ("DEPLOY" in brand blue, "HUB" in accent red).
 *
 * The mark is an inline, transparent SVG — NOT the raster brand asset, which
 * bakes the "DEPLOYHUB" text into the image. Using the icon-only mark avoids
 * (a) the word appearing twice inside the lockup and (b) a dark logo chip on
 * light surfaces. It also stays crisp at any size and adapts to both themes.
 *
 * Accessibility: the SVG is decorative (`aria-hidden`); the adjacent text is
 * the accessible name, so screen readers announce a single "DEPLOYHUB".
 */
export function Wordmark({ logoSize = 28, className = '', textClassName = '' }: WordmarkProps): JSX.Element {
  return (
    <span className={`inline-flex items-center gap-2.5 ${className}`.trim()}>
      <svg
        viewBox="0 0 64 64"
        width={logoSize}
        height={logoSize}
        className="shrink-0"
        aria-hidden="true"
        focusable="false"
      >
        {/* Tilted orbital ring (brand blue). */}
        <ellipse
          cx="32"
          cy="32"
          rx="26"
          ry="12"
          transform="rotate(-30 32 32)"
          fill="none"
          stroke="#1f6feb"
          strokeWidth="4"
          strokeLinecap="round"
        />
        {/* Core node (brand blue). */}
        <circle cx="32" cy="32" r="9" fill="#1f6feb" />
        {/* Orbiting deployment node (accent red). */}
        <circle cx="53" cy="21" r="5.5" fill="#e11d2a" />
      </svg>
      <span className={`text-xl font-bold tracking-tight ${textClassName}`.trim()}>
        <span className="text-brand-500">DEPLOY</span>
        <span className="text-accent-500">HUB</span>
      </span>
    </span>
  );
}
